import type { IScannerControls } from "@zxing/browser";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Stack, styled, Typography } from "~/common/mui";
import Modal from "~/common/components/Modal";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import CustomModalHeader from "~/features/scanDmc/components/CustomModalHeader/CustomModalHeader";
import { Trans, useTranslation } from "react-i18next";
import { IconSize } from "~/common/mui/icons/IconSize";
import { type OriginCheckResult, ScanResultType } from "~/features/scanDmc/types/ScanDMC";
import { fetchScanDMCOriginCheck } from "~/features/scanDmc/api/fetchScanDMCOriginCheck";
import { trackDmcScan } from "~/app/analytics/ga/ga";
import { useDMCScanner } from "~/features/scanDmc/components/DMCScanPage/DMCScanner/useDMCScanner";
import CenterContents from "~/common/components/CenterContents";
import { BackLink, LinkButton } from "~/common/components/Link";
import { IMG_UPLOAD_IMAGE } from "~/utils/url/staticResources";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { Alert } from "~/common/mui/Alert/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import FlashlightOffIcon from "~/common/mui/icons/FlashlightOff";
import FlashlightOnIcon from "~/common/mui/icons/FlashlightOn";

const ScanFooter = styled("div")({
  display: "flex",
  "& > *": {
    margin: "-12px",
  },
  "& > :last-child": {
    marginLeft: "auto",
  },
});

const VideoContainer = styled("div")({
  display: "flex",
  position: "relative",
  justifyContent: "center",
  alignItems: "center",
  flex: 1,
  width: "100%",
  background: "black",
  overflow: "hidden",
  minHeight: "300px",
});

const VideoOverlay = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  display: "grid",
  placeItems: "center",
  "& > svg": {
    maxWidth: "50%",
    maxHeight: "50%",
    "& > path": {
      strokeWidth: 2,
      stroke: "white",
      fill: "none",
    },
  },
});

const StyledVideo = styled("video")({
  width: "100%",
  position: "absolute",
  left: 0,
  top: "50%",
  transform: "translateY(-50%)",
  minHeight: "100%",
  objectFit: "cover",
});

interface Props {
  onScanResult: (result: OriginCheckResult) => Promise<void>;
  onShowHelp: () => void;
  oneCodeImplUrl?: string;
}

export const DMCScanPage = (props: Props) => {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const { val: modalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [scannedBefore, setScannedBefore] = useState(false);

  const { loading: loadingScanner, scanner } = useDMCScanner({ oneCodeImplUrl: props.oneCodeImplUrl });

  const [isDecodingImage, setIsDecodingImage] = useState(false);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);

  const closeModalCb = () => {
    closeModal();
    setScanned(false);
  };

  const toggleTorch = () => {
    if (!controlsRef.current?.switchTorch) {
      return;
    }
    controlsRef.current.switchTorch(!torchEnabled);
    setTorchEnabled(!torchEnabled);
  };

  const scanDMCCode = async (text: string) => {
    // It's an outer packaging
    if (isOuterPackagingCode(text)) {
      trackDmcScan(ScanResultType.OuterPackaging, text);
      openModal();
      return;
    }

    // Not outer packaging, let's ask the server
    setLoading(true);

    try {
      const result = await fetchScanDMCOriginCheck(text);

      const { type, dmcCode, productCode } = result;
      trackDmcScan(type, dmcCode, productCode);
      await props.onScanResult(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!scanner || !video.current || scanned) {
        return;
      }

      let decodePromise: Promise<string | null | undefined>;
      if (scannedBefore) {
        decodePromise = scanner.decodeOnce(video.current);
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1920 },
              // frameRate: { ideal: 30 },
              facingMode: "environment",
            },
          });

          scanner.reset();
          decodePromise = scanner.attachStreamToVideo(stream, video.current!).then((el) => {
            setShowCamera(true);
            return scanner.decodeOnce(el);
          });
        } catch (e: unknown) {
          setIsPermissionDenied(true);
          return;
        }
      }
      decodePromise.then((code) => {
        if (code) {
          setScanned(true);
          setScannedBefore(true);
          scanDMCCode(code);
        }
      });
    })();
  }, [scanner, video.current, scanned]);

  const fromFileCb = () => {
    if (!scanner || !fileInputRef.current) {
      return;
    }
    fileInputRef.current.click();
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      try {
        setIsDecodingImage(true);
        const result = await scanner?.decodeFromFile(file);
        if (result) {
          scanDMCCode(result);
        }
      } catch (e: unknown) {
        props.onScanResult({ type: ScanResultType.NoCodeDetected, dmcCode: "" });
      } finally {
        setIsDecodingImage(false);
      }
    }
  };

  const footer = (
    <ScanFooter>
      {torchAvailable && (
        <IconButton onClick={toggleTorch} color={torchEnabled ? "primary" : "secondary"}>
          {torchEnabled ? <FlashlightOnIcon sx={{ fontSize: 24 }} /> : <FlashlightOffIcon sx={{ fontSize: 24 }} />}
        </IconButton>
      )}

      <IconButton onClick={props.onShowHelp} color={"primary"}>
        <InfoOutlinedIcon sx={{ fontSize: 24 }} />
      </IconButton>
    </ScanFooter>
  );

  const showLoading = loading || loadingScanner || isDecodingImage;

  return (
    <>
      {modalOpen && (
        <Box sx={{ margin: "auto 16px !important", width: "auto !important", borderRadius: "4px" }}>
          <Modal hideClose onClose={closeModalCb} modalClassName="scha__modal__contents">
            <Stack sx={{ gap: 2 }}>
              <Alert severity={"info"}>
                <Trans i18nKey="page.scandmc.message.outerPackaging" />
              </Alert>

              <Button onClick={closeModalCb}>{t("page.scandmc.form.button.close")}</Button>
            </Stack>
          </Modal>
        </Box>
      )}
      <Modal
        headerOverride={<CustomModalHeader />}
        modalClassName="scha__modal__full-screen-view scha__modal--scanner"
        onClose={() => undefined}
        footerChildren={!showLoading ? footer : undefined}
      >
        {showLoading && (
          <CenterContents className={"w-100"}>
            <CircularProgress sx={{ position: "absolute", inset: 0, margin: "auto" }} size={64} />
          </CenterContents>
        )}
        {!showLoading && (
          <Box sx={{ height: "100%", display: "flex", flexFlow: "column" }}>
            <BackLink className={"mb-30"} iconSize={IconSize.FONT_24} href={HOMEPAGE}>
              {t("navigation.home")}
            </BackLink>
            <input type={"file"} ref={fileInputRef} onChange={onFileChange} hidden={true} />

            <Box
              className={isPermissionDenied ? "w-100" : undefined}
              sx={{
                flex: 1,
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                ...(!isPermissionDenied && { margin: "0 -16px" }),
                ...(isPermissionDenied && { height: "100%", justifyContent: "center", margin: 0 }),
              }}
            >
              {!isPermissionDenied && (
                <>
                  <VideoContainer>
                    <StyledVideo ref={video} style={{ visibility: showCamera ? "visible" : "hidden" }} />
                    <VideoOverlay>
                      <svg viewBox="0 0 100 100">
                        <path d="M25,2 L2,2 L2,25" />
                        <path d="M2,75 L2,98 L25,98" />
                        <path d="M75,98 L98,98 L98,75" />
                        <path d="M98,25 L98,2 L75,2" />
                      </svg>
                    </VideoOverlay>
                  </VideoContainer>
                  <Typography sx={{ textAlign: "center", mt: 2, mb: 3 }}>{t("page.scandmc.or")}</Typography>
                  <LinkButton
                    iconBefore={
                      <>
                        <CircularProgress size={20} hidden={!isDecodingImage} />
                        {!isDecodingImage && <NoteAddIcon />}
                      </>
                    }
                    disabled={isDecodingImage}
                    onClick={fromFileCb}
                  >
                    {t("page.scandmc.button.uploadImage")}
                  </LinkButton>
                </>
              )}

              {isPermissionDenied && (
                <>
                  <img src={IMG_UPLOAD_IMAGE} alt={""} />
                  <Button startIcon={<NoteAddIcon />} disabled={isDecodingImage} onClick={fromFileCb} fullWidth>
                    {t("page.scandmc.button.uploadImage")}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        )}
      </Modal>
    </>
  );
};

const OUTER_PACKAGING_CODES = ["02", "\u001d02"];

function isOuterPackagingCode(code: string) {
  return OUTER_PACKAGING_CODES.some((prefix) => code.startsWith(prefix));
}
